'use client'

import { BlockHeading } from '@/components/BlockHeading'
import { PayloadRichText } from '@/components/PayloadRichText'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import type { HowItWorksSectionBlock } from '@/payload-types'
import { hasRichText } from '@/utilities/hasRichText'

type HowItWorksSectionBlockProps = HowItWorksSectionBlock

type Step = NonNullable<HowItWorksSectionBlock['steps']>[number]

function StepCard({ step }: { step: Step }) {
  const hasStepTitle = Boolean(step.title?.trim())
  const hasStepDescription = hasRichText(step.description)

  if (!hasStepTitle && !hasStepDescription) return null

  return (
    <article className="flex flex-col gap-1.5 md:gap-2">
      {hasStepTitle ? (
        <h3 className="text-foreground text-sm font-semibold md:text-base lg:text-lg">{step.title}</h3>
      ) : null}
      {hasStepDescription ? (
        <PayloadRichText
          className="text-muted-foreground max-w-none text-sm md:text-base [&_a]:underline [&_p]:m-0 [&_p+p]:mt-1.5 md:[&_p+p]:mt-2"
          data={step.description}
        />
      ) : null}
    </article>
  )
}

export function HowItWorksSectionBlock(props: HowItWorksSectionBlockProps) {
  const hasTagline = Boolean(props.tagline?.trim())
  const hasHeading = Boolean(props.heading?.trim())
  const hasDescription = hasRichText(props.description)
  const headingId = hasHeading ? 'how-it-works-heading' : undefined
  const steps = props.steps ?? []

  if (steps.length === 0 && !hasTagline && !hasHeading && !hasDescription) return null

  return (
    <section aria-labelledby={headingId} className="bg-background">
      <div className="container-padding-x container mx-auto flex flex-col gap-6 md:gap-10 lg:gap-12">
        <div className="mx-auto flex max-w-xl flex-col items-center gap-3 text-center md:gap-5">
          {hasTagline ? <p className="age-type-eyebrow">{props.tagline}</p> : null}
          {hasHeading ? (
            <BlockHeading className="text-foreground" headingStyle={props.headingStyle} id={headingId}>
              {props.heading}
            </BlockHeading>
          ) : null}
          {hasDescription ? (
            <PayloadRichText
              className="text-muted-foreground max-w-none text-sm md:text-base [&_a]:underline [&_p]:m-0 [&_p+p]:mt-2"
              data={props.description}
            />
          ) : null}
        </div>

        {steps.length > 0 ? (
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 md:hidden">
            {steps.map((step, index) => (
              <StepCard key={step.id ?? `${step.title}-${index}`} step={step} />
            ))}
          </div>
        ) : null}

        {steps.length === 1 ? (
          <div className="mx-auto hidden w-full max-w-2xl md:block">
            <StepCard step={steps[0]} />
          </div>
        ) : null}

        {steps.length > 1 ? (
          <div className="relative hidden md:block md:px-14 lg:px-16">
            <Carousel
              aria-label="How it works steps"
              className="w-full"
              opts={{
                align: 'start',
                loop: false,
                slidesToScroll: 1,
              }}
            >
              <CarouselContent className="-ml-6">
                {steps.map((step, index) => (
                  <CarouselItem
                    className="min-w-0 basis-[47.5%] pl-6 xl:basis-[calc(95%/3)]"
                    key={step.id ?? `${step.title}-${index}`}
                  >
                    <StepCard step={step} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious
                className="border-foreground/20 -left-10 z-10 lg:-left-12"
                colorMode="light"
                variant="outline"
              />
              <CarouselNext
                className="border-foreground/20 -right-10 z-10 lg:-right-12"
                colorMode="light"
                variant="outline"
              />
            </Carousel>
          </div>
        ) : null}
      </div>
    </section>
  )
}
